
import BlueCheckbox from '@/components/BlueCheckbox.vue';
import languagesList from '@/resources/languagesList';
import store from '@/store';
import helpers from '@/logic/helpers/forms/onboardingHelpers';
import requestAPI from '@/logic/requestBeApi';
import { refreshTokenHandler } from '@/logic/auth';
import inputValidators from '@/logic/helpers/inputs/validators';
import ServerResponseAlert from '@/components/ServerResponseAlert.vue';
import passDataToResponseAlert from '@/logic/helpers/api/passDataToResponseAlert';

export default {
  name: 'AssessorOnboardingBC',

  components: {
    BlueCheckbox,
    ServerResponseAlert,
  },

  props: {
    isPartOfProfile: Boolean,
    focusOnProgressCircle: Boolean,
  },

  data(): Record<string, unknown> {
    return {
      onboardingBC: {
        assessmentPrice: undefined,
        acceptableDiscount: undefined,
        distanceOfWork: undefined,
        workingDays: [],
        startTime: undefined,
        endTime: undefined,
        languagesSpoken: [],
        languagesWritten: [],
      },
      validationRules: {
        assessmentPrice: [{ validator: inputValidators.primitive, trigger: 'change' }],
        workingDays: [{ validator: inputValidators.structural, trigger: 'change' }],
        startTime: [{ validator: inputValidators.primitive, trigger: 'change' }],
        endTime: [{ validator: inputValidators.primitive, trigger: 'change' }],
        languagesSpoken: [{ validator: inputValidators.structural, trigger: 'change' }],
        languagesWritten: [{ validator: inputValidators.structural, trigger: 'change' }],
      },
      distanceOfWorkSetsToMax: false,
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
      languagesList,
      processingUserDataRequest: false,
      saveAndQuit: false,
      serverResponse: null,
      formIsValid: false,
    };
  },

  computed: {
    workingHours(): string {
      return `${this.onboardingBC.startTime}-${this.onboardingBC.endTime}`;
    },

    computedDistanceOfWork: {
      get(): number {
        return !this.onboardingBC.distanceOfWork ? 1 : this.onboardingBC.distanceOfWork;
      },

      set(newVal: number): number {
        this.onboardingBC.distanceOfWork = newVal;
        return newVal;
      },
    },

    workingDays(): string[] {
      return this.onboardingBC.workingDays;
    },

    languagesSpoken(): string[] {
      return this.onboardingBC.languagesSpoken;
    },

    languagesWritten(): string[] {
      return this.onboardingBC.languagesWritten;
    },
  },

  watch: {
    workingDays(): void {
      this.$refs['workingDays'].blur();
    },

    languagesSpoken(): void {
      this.$refs['languagesSpoken'].blur();
      this.prefillLangs();
    },

    languagesWritten(): void {
      this.$refs['languagesWritten'].blur();
    },
  },

  mounted(): void {
    this.populateForms();
  },

  methods: {
    submitForm(saveOption: 'saveAndQuit' | 'saveAndContinue'): void {
      helpers.validateAndSubmitUserData(this, 'onboardingBCForm', saveOption);
    },

    populateForms(): void {
      if (this.isPartOfProfile) {
        this.getBusinessConditions();
      } else {
        this.onboardingBC.workingDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
      }
    },

    async getBusinessConditions(): Promise<void> {
      const resp = await requestAPI({
        url: '/api/v1/assessors/me/business-conditions',
        method: 'GET',
      });

      if (resp.ok) {
        const data = await resp.json();

        this.onboardingBC.assessmentPrice = data.assessment_price;
        this.onboardingBC.acceptableDiscount = data.acceptable_discount;
        this.onboardingBC.distanceOfWork = data.distance_of_work;
        this.onboardingBC.workingDays = data.working_days;
        this.onboardingBC.languagesSpoken = data.languages_spoken;
        this.onboardingBC.languagesWritten = data.languages_written;
        this.onboardingBC.startTime = data.working_hours?.split('-')[0];
        this.onboardingBC.endTime = data.working_hours?.split('-')[1];

        if (data.distance_of_work === 100000) {
          this.distanceOfWorkSetsToMax = true;
        }
      } else if (resp.status !== 401) {
        const data = await passDataToResponseAlert(resp);
        this.serverResponse = data;
      }
    },

    prefillLangs(): void {
      this.onboardingBC.languagesWritten = this.onboardingBC.languagesSpoken;

      this.setUserLangToStore();
    },

    setUserLangToStore(): void {
      store.commit('setUserLang', this.languagesSpoken);
    },

    async patchUserDataWhileOnProfile(payload: unknown): Promise<void> {
      this.processingUserDataRequest = true;

      const resp = await requestAPI({
        url: '/api/v1/assessors/me/business-conditions',
        method: 'PATCH',
        contentType: 'application/merge-patch+json',
        payload,
      });

      if (resp.ok) {
        const makeImmediateRefreshTokenCall = true;
        const clearOldRefrTokenTimeout = true;
        refreshTokenHandler(makeImmediateRefreshTokenCall, clearOldRefrTokenTimeout);

        if (!this.focusOnProgressCircle) {
          this.$message.success(this.$t('businessInfoUpdated'));
        }

        this.$emit('runGetProfileCompletionProcess');
      } else if (resp.status !== 401) {
        const data = await passDataToResponseAlert(resp);
        this.serverResponse = data;
      }

      this.processingUserDataRequest = false;
    },

    async submitUserData(): Promise<void> {
      this.serverResponse = {};
      this.processingUserDataRequest = true;

      const payload = {
        assessment_price: this.onboardingBC.assessmentPrice,
        acceptable_discount: this.onboardingBC.acceptableDiscount || 0,
        distance_of_work: this.distanceOfWorkSetsToMax ? 100000 : this.computedDistanceOfWork,
        working_days: this.onboardingBC.workingDays,
        working_hours: this.workingHours,
        languages_spoken: this.languagesSpoken,
        languages_written: this.languagesWritten,
      };

      if (this.isPartOfProfile) {
        this.patchUserDataWhileOnProfile(payload);
        return;
      }

      const resp = await requestAPI({
        method: 'POST',
        url: '/api/v1/onboard/business-conditions',
        payload,
      });

      if (resp.ok) {
        const makeImmediateRefreshTokenCall = true;
        const clearOldRefrTokenTimeout = true;
        refreshTokenHandler(makeImmediateRefreshTokenCall, clearOldRefrTokenTimeout);

        this.saveAndQuit ? helpers.endOnboarding() : this.$emit('setStep', 'company', resp);
      } else {
        this.saveAndQuit = false;
        const data = await passDataToResponseAlert(resp);
        this.serverResponse = data;
      }

      this.processingUserDataRequest = false;
    },

    goNextScreen(): void {
      this.$emit('setStep', 'company');

      localStorage.setItem('notifiedAboutIncompleteStage2', 'true');

      this.$notify.info({
        title: this.$t('info'),
        message: this.$t('skipStage2OfAssessorOnboardingInfo'),
        duration: 0,
        offset: 120,
      });
    },
  },
};
